import React from "react";
import { Progress } from "@/components/ui/progress";
import { GradientButton } from "@/components/ui/GradientButton";
import {
  useBackendHealth,
  BACKEND_STATUS,
} from "@/contexts/BackendHealthContext";

/**
 * TeamTabLoadingScreen - Branded loading screen for backend cold starts
 * Shows playful messages and progress during backend initialization
 */
export default function TeamTabLoadingScreen() {
  const { backendStatus, healthCheckAttempt, progress, retry } =
    useBackendHealth();

  /**
   * Get contextual message based on backend status and attempt number
   */
  const getMessage = () => {
    if (backendStatus === BACKEND_STATUS.FAILED) {
      return {
        title: "😅 Oops!",
        subtitle:
          "Looks like our tab is stuck in a filing cabinet from 1987...",
        showRetry: true,
      };
    }

    if (backendStatus === BACKEND_STATUS.DEGRADED) {
      return {
        title: "Tab's being stubborn. Retrying... 🔄",
        subtitle: `Attempt ${healthCheckAttempt} of 6`,
        showRetry: false,
      };
    }

    // Connecting state - show different messages based on attempt
    if (healthCheckAttempt <= 2) {
      return {
        title: "Opening your tab... 🔓",
        subtitle: "Just a moment...",
        showRetry: false,
      };
    } else if (healthCheckAttempt <= 4) {
      return {
        title: "Almost there... the tab's a bit stuck... 📂",
        subtitle: `Attempt ${healthCheckAttempt} of 6`,
        showRetry: false,
      };
    } else {
      return {
        title: "This tab is really stuck! Hang tight... 🔨",
        subtitle: `Attempt ${healthCheckAttempt} of 6`,
        showRetry: false,
      };
    }
  };

  const { title, subtitle, showRetry } = getMessage();

  /**
   * Render progress dots (visual indicator of attempts)
   */
  const renderProgressDots = () => {
    return (
      <div className="flex gap-2 justify-center">
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index < healthCheckAttempt
                ? "bg-blue-600 scale-110"
                : "bg-gray-300 scale-100"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 text-center">
        {/* Animated Tab Icon */}
        <div className="flex justify-center">
          {backendStatus === BACKEND_STATUS.FAILED ? (
            // Static sad tab for error state
            <div className="text-8xl animate-bounce">📁</div>
          ) : (
            // Animated opening tab
            <div className="relative">
              <div className="text-8xl animate-tab-open">🗂️</div>
              {/* Pulsing glow effect */}
              <div className="absolute inset-0 blur-xl opacity-30 animate-pulse">
                <div className="w-full h-full bg-blue-400 rounded-full"></div>
              </div>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {!showRetry && (
          <div className="space-y-3">
            <Progress
              value={progress}
              className="h-3 bg-blue-100 dark:bg-blue-900"
            />
            {renderProgressDots()}
          </div>
        )}

        {/* Messages */}
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {title}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">{subtitle}</p>
        </div>

        {/* Retry Button (only shown on failure) */}
        {showRetry && (
          <div className="space-y-4 pt-4">
            <GradientButton
              variant="primary"
              size="lg"
              onClick={retry}
              className="w-full max-w-xs mx-auto"
            >
              🔨 Give It Another Pull
            </GradientButton>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              (Or maybe try refreshing the page)
            </p>
          </div>
        )}

        {/* Loading indicator for connecting state */}
        {!showRetry && (
          <div className="flex justify-center">
            <div className="flex space-x-1">
              <div
                className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
